import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { GatsbyImage } from 'gatsby-plugin-image';
import Lottie from 'react-lottie-player';

import { Overline } from '../layouts/Section';
import HTMLWrapper from '../HTMLWrapper';
import Text from '../Text';
import StatBlock from '../StatBlock';
import BasicButton from '../BasicButton';
import Link from '../BasicLink';
import { Cell, Grid } from '../layouts/Grid';
import WistiaVideo from '../WistiaVideo';

import theme from '../../../styles/theme';
import documentToReactComponents from '../../util/documentToReactComponents';

const {
	breakPoints: { screenQuery },
	font,
	colors,
} = theme;

const SingleImageGrid = styled(Grid)`
	h3 {
		font-family: ${font.recoleta};
		font-size: 28px;
		line-height: 40px;
	}

	.single_image_block__mainImg {
		width: 100%;
	}

	a {
		margin-bottom: 20px;
	}

	p {
		margin-bottom: 1rem;
	}

	ul {
		padding: 0;
		margin: 15px 0;

		li {
			width: 100%;
			padding-left: 1em;
			margin-bottom: 15px;
			position: relative;

			&::before {
				content: '•';
				position: absolute;
				vertical-align: top;
				font-size: 1.25em;
				color: rgb(96, 99, 104);
				margin-left: -0.8em;
			}
		}
	}
`;

const Caption = styled(Text)`
	width: 100%;
	background-color: ${colors.grayscale.main};
	color: ${colors.grayscale.white};
	padding: 4px 10px;

	text-align: center;
	font-size: 14px;
	line-height: 14px;

	display: inline-block;
	margin: 0 auto;
`;

const SpacingCell = styled(Cell)`
	img {
		width: 100%;
	}

	${screenQuery.small} {
		padding-bottom: 30px;
	}
`;

const InfoBlock = styled.div`
	border-left: 2px solid #d2d4d8;
	padding: 17px 24px;
	margin-top: 24px;
	text-align: left;

	img {
		display: block;
		max-width: 26px;
		margin-bottom: 8px;
	}
	p {
		font-size: 15px;

		a {
			color: #333333;
			font-family: ${theme.font.regular};
			font-weight: 400;
			text-decoration: underline;
		}
	}
`;

const SingleImageBlock = ({
	image,
	imageCaption,
	imageLink,
	lottieLoop,
	orangeButton,
	arrow,
	html,
	raw,
	headerHead,
	header,
	subheader,
	buttonText,
	buttonLink,
	additionalLinks,
	flipX,
	noImgPadding,
	gridClass,
	statSection,
	statblockLogo,
	lineStyle,
	boxStyle,
	sectionColor,
	stackedBlock,
	spanA,
	spanB,
	spanAStart,
	spanBStart,
	className,
	video,
	...props
}) => {
	const [jsonFile, setJSONFile] = useState();
	const imageFileEnd = typeof image !== 'object' && image?.split('.').pop();

	useEffect(() => {
		if (imageFileEnd === 'json') {
			fetch(image)
				.then(res => res.json())
				.then(data => setJSONFile(data));
		}
	}, []);

	return (
		<SingleImageGrid
			className={`${gridClass} single-image-grid ${className}`}
			noSmallGap
		>
			{video && (
				<SpacingCell
					css={{ height: '100%', width: '100%' }}
					span={spanA || 6}
					start={spanAStart || (flipX ? 7 : 1)}
					rowStartSm={1}
					spanSm={8}
					startSm={3}
					className="vertical-center single_image_block__mainImg"
				>
					<WistiaVideo {...video} />
				</SpacingCell>
			)}
			{image && (
				<SpacingCell
					css={{ height: '100%', width: '100%' }}
					span={spanA || 6}
					start={spanAStart || (flipX ? 7 : 1)}
					rowStartSm={1}
					spanSm={8}
					startSm={3}
					className="vertical-center single_image_block__mainImg"
				>
					{image.gatsbyImageData ? (
						<GatsbyImage
							className="singleImageBlock_imageContainer"
							image={image.gatsbyImageData}
						/>
					) : imageFileEnd === 'json' ? (
						<Lottie animationData={jsonFile} loop={lottieLoop} play={true} />
					) : imageLink ? (
						<Link to={imageLink} linkWrapper>
							<img
								className="singleImageBlock_imageContainer"
								src={image?.file?.url || image}
							/>
						</Link>
					) : (
						<img
							className="singleImageBlock_imageContainer"
							src={image?.file?.url || image}
						/>
					)}
					{imageCaption && <Caption component="b5">{imageCaption}</Caption>}
				</SpacingCell>
			)}
			<Cell
				align="center"
				span={spanB || 6}
				start={spanBStart || (flipX ? 1 : 7)}
				rowStart={flipX ? 1 : null}
				rowStartSm={2}
			>
				{headerHead && (
					<Overline component="caps" className="image-block-overline">
						{headerHead}
					</Overline>
				)}
				{header ? (
					<div>
						<Text
							component={stackedBlock ? 'h3' : 'h2'}
							className="image-block__header"
						>
							{header}
						</Text>
						{subheader &&
							(typeof subheader === 'object' ? (
								<Text component="b4" className="image-block__subheader">
									{documentToReactComponents(subheader)}
								</Text>
							) : (
								<Text component="b4" className="image-block__subheader">
									<HTMLWrapper oneColumnList html={subheader} />
								</Text>
							))}
						{raw && (
							<HTMLWrapper className="image-block__subheader">
								{documentToReactComponents(raw)}
							</HTMLWrapper>
						)}
					</div>
				) : raw ? (
					<HTMLWrapper>{documentToReactComponents(raw)}</HTMLWrapper>
				) : null}

				{additionalLinks
					? additionalLinks?.map(link => {
							return (
								<BasicButton
									className="single-image-block__button"
									style={{ marginTop: '10px' }}
									arrow={!orangeButton}
									secondary={!orangeButton}
									to={link.linkUrl}
								>
									{link.linkText}
								</BasicButton>
							);
						})
					: ''}
				{statSection && (
					<StatBlock
						statSection={statSection}
						logo={statblockLogo}
						styleType={boxStyle && 'box'}
					/>
				)}
				{raw?.references?.map(reference => {
					if (reference.type === 'ContentfulStatBlock') {
						return (
							<StatBlock
								individualStat={reference.statistic}
								individualStatDescription={
									reference.statisticDescription.childMarkdownRemark.html
								}
								logo={reference?.statisticIcon?.file.url}
								styleType={lineStyle ? 'line' : 'box'}
							/>
						);
					}
				})}
			</Cell>
		</SingleImageGrid>
	);
};

export default SingleImageBlock;
